// src/pages/OrganizationStructurePage.jsx
// ✅ Gestione ruoli custom per ogni organizzazione
import React, { useState, useEffect, useCallback } from 'react';
import axiosConfig from '../api/axiosConfig';
import {
    PlusIcon, PencilSquareIcon, TrashIcon, UserGroupIcon,
    ArrowPathIcon, CheckCircleIcon, SparklesIcon,
    ShieldCheckIcon, ChevronUpIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// ==================== API ====================
const api = {
    getRoles: () => axiosConfig.get('/custom-roles'),
    createRole: (data) => axiosConfig.post('/custom-roles', data),
    updateRole: (id, data) => axiosConfig.put(`/custom-roles/${id}`, data),
    deleteRole: (id) => axiosConfig.delete(`/custom-roles/${id}`),
    getUsers: () => axiosConfig.get('/custom-roles/users'),
    assignRole: (data) => axiosConfig.post('/custom-roles/assign', data),
    removeRole: (userId, roleId) => axiosConfig.delete(`/custom-roles/users/${userId}/roles/${roleId}`),
    getTemplates: () => axiosConfig.get('/custom-roles/templates'),
    applyTemplate: (template) => axiosConfig.post('/custom-roles/apply-template', { template }),
};

// ==================== PERMESSI ====================
const PERMISSIONS = [
    { key: 'canManageUsers', label: 'Gestisce utenti', group: 'Team' },
    { key: 'canInviteUsers', label: 'Invita utenti', group: 'Team' },
    { key: 'canManageProjects', label: 'Gestisce progetti', group: 'Progetti' },
    { key: 'canCreateExpenses', label: 'Crea spese', group: 'Finanza' },
    { key: 'canValidateExpenses', label: 'Valida spese', group: 'Finanza' },
    { key: 'canViewFinancials', label: 'Vede finanze', group: 'Finanza' },
    { key: 'canManageProcurement', label: 'Gestisce acquisti', group: 'Acquisti' },
    { key: 'canUseAI', label: 'Usa AI', group: 'AI' },
    { key: 'canViewReports', label: 'Vede report', group: 'Report' },
    { key: 'canExportData', label: 'Esporta dati', group: 'Report' },
    { key: 'canManageHSE', label: 'Gestisce HSE', group: 'HSE' },
    { key: 'isExternal', label: 'Utente esterno', group: 'Tipo' },
];

const GROUPS = [...new Set(PERMISSIONS.map(p => p.group))];

const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#22c55e', '#10b981',
    '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#6b7280'];

const DEFAULT_ROLE = {
    name: '', description: '', color: '#6366f1', level: 5,
    canManageUsers: false, canInviteUsers: false, canManageProjects: true,
    canCreateExpenses: true, canValidateExpenses: false, canViewFinancials: false,
    canManageProcurement: false, canUseAI: false, canViewReports: true,
    canManageHSE: false, canExportData: false, isExternal: false,
};

// ==================== ROLE CARD ====================
const RoleCard = ({ role, onEdit, onDelete, onSelect, selected }) => (
    <div onClick={() => onSelect(role)}
        className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all ${selected ? 'border-blue-400 shadow-md' : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'}`}>
        <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ background: role.color }}>
                    {role.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{role.name}</p>
                    <p className="text-xs text-gray-400 truncate">{role.description || 'Nessuna descrizione'}</p>
                </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Lv.{role.level}</span>
                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{role._count?.users || 0} utenti</span>
            </div>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
            {PERMISSIONS.filter(p => role[p.key]).slice(0, 4).map(p => (
                <span key={p.key} className="text-xs bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">{p.label}</span>
            ))}
            {PERMISSIONS.filter(p => role[p.key]).length > 4 && (
                <span className="text-xs text-gray-400">+{PERMISSIONS.filter(p => role[p.key]).length - 4}</span>
            )}
        </div>
        <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100">
            <button onClick={e => { e.stopPropagation(); onEdit(role); }}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium">
                <PencilSquareIcon className="w-3.5 h-3.5" /> Modifica
            </button>
            <button onClick={e => { e.stopPropagation(); onDelete(role); }}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 font-medium">
                <TrashIcon className="w-3.5 h-3.5" /> Elimina
            </button>
        </div>
    </div>
);

// ==================== ROLE FORM ====================
const RoleForm = ({ role, onSave, onCancel }) => {
    const [form, setForm] = useState(role || DEFAULT_ROLE);
    const [loading, setLoading] = useState(false);

    const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Nome richiesto'); return; }
        setLoading(true);
        try {
            if (form.id) {
                await api.updateRole(form.id, form);
                toast.success('Ruolo aggiornato');
            } else {
                await api.createRole(form);
                toast.success('Ruolo creato');
            }
            onSave();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore salvataggio');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border p-6 space-y-5">
            <h3 className="font-bold text-gray-900">{form.id ? 'Modifica Ruolo' : 'Nuovo Ruolo'}</h3>

            <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome ruolo *</label>
                    <input type="text" value={form.name} onChange={e => set('name', e.target.value)}
                        placeholder="es. Capo Cantiere"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div className="col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Descrizione</label>
                    <input type="text" value={form.description || ''} onChange={e => set('description', e.target.value)}
                        placeholder="es. Supervisiona i lavori in cantiere"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Livello gerarchia</label>
                    <div className="flex items-center gap-2">
                        <button onClick={() => set('level', Math.max(1, (form.level || 5) - 1))}
                            className="w-8 h-8 rounded-lg border flex items-center justify-center hover:bg-gray-50">
                            <ChevronUpIcon className="w-4 h-4" />
                        </button>
                        <span className="text-lg font-bold text-gray-900 w-8 text-center">{form.level}</span>
                        <button onClick={() => set('level', Math.min(10, (form.level || 5) + 1))}
                            className="w-8 h-8 rounded-lg border flex items-center justify-center hover:bg-gray-50">
                            <ChevronDownIcon className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-gray-400">1=top, 10=bottom</span>
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Colore</label>
                    <div className="flex flex-wrap gap-1.5">
                        {COLORS.map(c => (
                            <button key={c} onClick={() => set('color', c)}
                                className={`w-6 h-6 rounded-full transition-transform ${form.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                                style={{ background: c }} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Permessi per gruppo */}
            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Permessi</label>
                <div className="space-y-3">
                    {GROUPS.map(group => (
                        <div key={group}>
                            <p className="text-xs font-semibold text-gray-400 mb-1.5">{group}</p>
                            <div className="grid grid-cols-2 gap-2">
                                {PERMISSIONS.filter(p => p.group === group).map(p => (
                                    <label key={p.key} className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={!!form[p.key]}
                                            onChange={e => set(p.key, e.target.checked)}
                                            className="w-4 h-4 text-blue-600 rounded" />
                                        <span className="text-sm text-gray-700">{p.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex gap-3 pt-2">
                <button onClick={handleSave} disabled={loading}
                    className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
                    {form.id ? 'Salva modifiche' : 'Crea ruolo'}
                </button>
                <button onClick={onCancel}
                    className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                    Annulla
                </button>
            </div>
        </div>
    );
};

// ==================== MAIN ====================
const OrganizationStructurePage = () => {
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('roles'); // roles | users | templates
    const [editRole, setEditRole] = useState(null);    // null | 'new' | role object
    const [selectedRole, setSelectedRole] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [r, u, t] = await Promise.all([api.getRoles(), api.getUsers(), api.getTemplates()]);
            setRoles(r.data.data || []);
            setUsers(u.data.data || []);
            setTemplates(t.data.data || []);
        } catch {
            toast.error('Errore caricamento');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const handleDelete = async (role) => {
        if (role._count?.users > 0) {
            toast.error(`Impossibile eliminare — ${role._count.users} utenti hanno questo ruolo`);
            return;
        }
        try {
            await api.deleteRole(role.id);
            toast.success('Ruolo eliminato');
            loadAll();
            if (selectedRole?.id === role.id) setSelectedRole(null);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore eliminazione');
        }
    };

    const handleApplyTemplate = async (templateId) => {
        try {
            const res = await api.applyTemplate(templateId);
            toast.success(res.data.message);
            loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore template');
        }
    };

    const handleAssignRole = async (userId, roleId) => {
        try {
            await api.assignRole({ userId, customRoleId: roleId });
            toast.success('Ruolo assegnato');
            loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore assegnazione');
        }
    };

    const handleRemoveRole = async (userId, roleId) => {
        try {
            await api.removeRole(userId, roleId);
            toast.success('Ruolo rimosso');
            loadAll();
        } catch {
            toast.error('Errore rimozione ruolo');
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <ArrowPathIcon className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <ShieldCheckIcon className="w-7 h-7 text-blue-500" />
                        Struttura Organizzazione
                    </h1>
                    <p className="text-gray-500 text-sm mt-0.5">Definisci ruoli e permessi personalizzati per la tua impresa</p>
                </div>
                {tab === 'roles' && !editRole && (
                    <button onClick={() => setEditRole('new')}
                        className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition">
                        <PlusIcon className="w-5 h-5" /> Nuovo Ruolo
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 gap-1">
                {[
                    { id: 'roles', label: `Ruoli (${roles.length})`, icon: ShieldCheckIcon },
                    { id: 'users', label: `Team (${users.length})`, icon: UserGroupIcon },
                    { id: 'templates', label: 'Template', icon: SparklesIcon },
                ].map(t => (
                    <button key={t.id} onClick={() => { setTab(t.id); setEditRole(null); }}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${tab === t.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                        <t.icon className="w-4 h-4" /> {t.label}
                    </button>
                ))}
            </div>

            {/* ── TAB RUOLI ── */}
            {tab === 'roles' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-3">
                        {roles.length === 0 ? (
                            <div className="bg-white rounded-2xl border p-10 text-center">
                                <ShieldCheckIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm mb-4">Nessun ruolo definito</p>
                                <p className="text-gray-400 text-xs mb-4">Usa un template per iniziare rapidamente o crea il tuo primo ruolo</p>
                                <button onClick={() => setTab('templates')}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition">
                                    Scegli un template
                                </button>
                            </div>
                        ) : (
                            roles.map(role => (
                                <div key={role.id} className="group">
                                    <RoleCard
                                        role={role}
                                        selected={selectedRole?.id === role.id}
                                        onSelect={setSelectedRole}
                                        onEdit={r => setEditRole(r)}
                                        onDelete={handleDelete}
                                    />
                                </div>
                            ))
                        )}
                    </div>

                    <div>
                        {editRole ? (
                            <RoleForm
                                role={editRole === 'new' ? null : editRole}
                                onSave={() => { setEditRole(null); loadAll(); }}
                                onCancel={() => setEditRole(null)}
                            />
                        ) : selectedRole ? (
                            <div className="bg-white rounded-2xl border p-5 space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-bold"
                                        style={{ background: selectedRole.color }}>
                                        {selectedRole.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">{selectedRole.name}</h3>
                                        <p className="text-xs text-gray-400">{selectedRole.description}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Permessi attivi</p>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {PERMISSIONS.map(p => (
                                            <div key={p.key} className={`flex items-center gap-1.5 text-xs py-1 px-2 rounded-lg ${selectedRole[p.key] ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-300'}`}>
                                                {selectedRole[p.key] ? '✅' : '○'} {p.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <button onClick={() => setEditRole(selectedRole)}
                                    className="w-full py-2 border border-blue-200 text-blue-600 rounded-xl text-sm font-semibold hover:bg-blue-50 transition">
                                    Modifica ruolo
                                </button>
                            </div>
                        ) : (
                            <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 p-10 text-center text-gray-400">
                                <p className="text-sm">Seleziona un ruolo per i dettagli</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── TAB UTENTI ── */}
            {tab === 'users' && (
                <div className="space-y-3">
                    {users.map(user => (
                        <div key={user.id} className="bg-white rounded-xl border p-4 flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                                {user.firstName?.[0]}{user.lastName?.[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900">{user.firstName} {user.lastName}</p>
                                <p className="text-xs text-gray-400">{user.email}</p>
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{user.role}</span>
                                    {user.customRoles?.map(cr => (
                                        <span key={cr.customRole.id}
                                            className="text-xs text-white px-2 py-0.5 rounded-full flex items-center gap-1"
                                            style={{ background: cr.customRole.color }}>
                                            {cr.customRole.name}
                                            <button onClick={() => handleRemoveRole(user.id, cr.customRole.id)}
                                                className="hover:opacity-70 ml-0.5">×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                            {roles.length > 0 && (
                                <select onChange={e => { if (e.target.value) { handleAssignRole(user.id, e.target.value); e.target.value = ''; } }}
                                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-gray-50 focus:ring-1 focus:ring-blue-500">
                                    <option value="">+ Assegna ruolo</option>
                                    {roles.filter(r => !user.customRoles?.find(cr => cr.customRole.id === r.id)).map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ── TAB TEMPLATE ── */}
            {tab === 'templates' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {templates.map(t => (
                        <div key={t.id} className="bg-white rounded-2xl border p-5">
                            <h3 className="font-bold text-gray-900 mb-1">{t.name}</h3>
                            <p className="text-xs text-gray-400 mb-3">{t.rolesCount} ruoli predefiniti</p>
                            <div className="flex flex-wrap gap-1.5 mb-4">
                                {t.roles.map((r, i) => (
                                    <span key={i} className="text-xs text-white px-2 py-0.5 rounded-full"
                                        style={{ background: r.color }}>
                                        {r.name}
                                    </span>
                                ))}
                            </div>
                            <button onClick={() => handleApplyTemplate(t.id)}
                                className="w-full py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition">
                                Usa questo template
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default OrganizationStructurePage;